<template>
  <div class="spv-card">
    <div class="spv-card__icon">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
        <polyline points="14 2 14 8 20 8"></polyline>
        <line x1="16" y1="13" x2="8" y2="13"></line>
        <line x1="16" y1="17" x2="8" y2="17"></line>
        <polyline points="10 9 9 9 8 9"></polyline>
      </svg>
    </div>

    <div class="spv-card__main">
      <div class="spv-card__info">
        <h3 class="spv-card__name">{{ spv.name || spv.proposed_name1 }}</h3>
        <p class="spv-card__managed">
          {{ spv.should_manage_spv ? 'Managed by GetEquity' : `Managed by ${orgName}` }}
        </p>
        <span class="spv-card__type" :class="spvTypeClass">{{ spvType }}</span>
      </div>
    </div>

    <div class="spv-card__details">
      <div class="spv-card__detail">
        <span class="spv-card__label">Country</span>
        <span class="spv-card__value">{{ spvCountry }}</span>
        <router-link
          v-if="spv.status === 'Pending'"
          :to="{ name: 'SpvBusinessInfo', params: { id: spv.id } }"
          class="spv-card__link"
        >
          Edit SPV Request
        </router-link>
        <router-link
          v-else
          :to="{ name: 'SpvInfo', params: { id: spv._id } }"
          class="spv-card__link"
        >
          View SPV
        </router-link>
      </div>
      <div class="spv-card__detail">
        <span class="spv-card__label">Date of Creation</span>
        <span class="spv-card__value">{{ dateOfCreation }}</span>
        <button
          v-if="spv.status === 'Pending'"
          @click="showSpvConfirmModal = true"
          class="spv-card__delete"
          :disabled="!isActionAllowed"
        >
          Cancel Request
        </button>
        <button
          v-else
          @click="showSpvConfirmModal = true"
          class="spv-card__delete"
          :disabled="!isActionAllowed"
        >
          Delete
        </button>
      </div>
    </div>

    <SpvInfo
      v-if="showSpvInfoModal"
      :spv="spv"
      @close-spv-info="showSpvInfoModal = false"
    />
    <ConfirmCancelModal
      :type="cancelType"
      v-if="showSpvConfirmModal"
      @confirm-cancel="getDeleteType"
      @close-cancel-modal="showSpvConfirmModal = false"
    />
    <SpvSuccessModal
      v-if="showSuccessModal"
      :type="cancelType"
      @close-success-modal="showSuccessModal = false"
    />
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import ConfirmCancelModal from "./ConfirmCancelModal.vue";
import SpvInfo from "./SpvInfo.vue";
import SpvSuccessModal from "./SpvSuccessModal.vue";

const store = useStore();

const { spv } = defineProps({
  spv: {
    type: Object,
    required: true,
  },
  isActionAllowed: Boolean,
});
const emits = defineEmits(["refresh-list"]);

const showSpvInfoModal = ref(false);
const showSpvConfirmModal = ref(false);
const showSuccessModal = ref(false);

const spvType = computed(() =>
  spv.meta ? "Uploaded SPV Doc" : getSpvStatusText()
);
const cancelType = computed(() =>
  spv.status === "Pending" ? "Request" : "Document"
);
const spvTypeClass = computed(() =>
  spvType.value === "Uploaded SPV Doc"
    ? "spv-card__type--uploaded"
    : getClassByStatus()
);

const spvCountry = computed(() => {
  let country = null;
  if (spv.meta?.country_of_incorporation) {
    country = spv.meta.country_of_incorporation?.split(" ")[0];
  } else {
    country = spv.country;
  }
  return country;
});

const dateOfCreation = computed(() => {
  let date = null;
  if (spv.meta?.date_of_creation) {
    date = spv.meta.date_of_creation.split("T")[0];
  } else {
    date = spv.createdAt.split("T")[0];
  }
  return date;
});

const orgName = computed(
  () => store.state.organisationModule.organisationDetails.name
);

const getSpvStatusText = () => {
  const status = spv.status;
  let statusText = null;
  switch (status) {
    case "Pending":
      statusText = "Pending Approval";
      break;
    case "Approved":
      statusText = "Approved";
      break;
    default:
      statusText = status;
      break;
  }
  return statusText;
};

const getDeleteType = () => {
  if (spv.status === "Pending") {
    cancelSpvRequest(spv._id);
  } else {
    deleteSpv(spv._id);
  }
};

const cancelSpvRequest = async () => {
  const id = spv._id;
  const response = await store.dispatch("Spv/cancelSpvRequest", id);
  const { message } = response.data;
  store.dispatch("showToast", {
    description: message,
    display: true,
    type: "success",
  });
  emits("refresh-list");
};

const deleteSpv = async (id) => {
  try {
    const response = await store.dispatch("Spv/deleteSpv", id);
    const { message } = response.data;
    store.dispatch("showToast", {
      description: message,
      display: true,
      type: "success",
    });
    emits("refresh-list");
  } catch (error) {}
};

const getClassByStatus = () => {
  const status = spv.status;
  let statusClass = null;
  switch (status) {
    case "Pending":
      statusClass = "spv-card__type--pending";
      break;
    case "Rejected":
      statusClass = "spv-card__type--rejected";
      break;
    case "Approved":
      statusClass = "spv-card__type--approved";
      break;
    default:
      statusClass = "spv-card__type--default";
      break;
  }
  return statusClass;
};
</script>

<style lang="scss" scoped>
// Design System Variables
$text-primary: #0F172A;
$text-secondary: #475569;
$text-muted: #64748B;
$bg-card: #FFFFFF;
$bg-subtle: #F1F5F9;
$border-color: #E2E8F0;
$accent-primary: #4D84FF;
$success: #10B981;
$error: #DC2626;
$warning: #D97706;

.spv-card {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 20px;
  align-items: start;
  background: $bg-card;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 20px 24px;
  margin-bottom: 12px;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: darken($border-color, 5%);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: $bg-subtle;
    color: $text-muted;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__main {
    min-width: 0;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__name {
    font-size: 16px;
    font-weight: 500;
    color: $text-primary;
    margin: 0;
  }

  &__managed {
    font-size: 13px;
    color: $text-muted;
    margin: 0;
  }

  &__type {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    border-radius: 7px;
    font-size: 12px;
    font-weight: 500;
    width: fit-content;
    margin-top: 12px;

    &--uploaded {
      background: rgba($accent-primary, 0.1);
      color: $accent-primary;
    }

    &--pending {
      background: rgba($warning, 0.1);
      color: $warning;
    }

    &--approved {
      background: rgba($success, 0.1);
      color: $success;
    }

    &--rejected {
      background: rgba($error, 0.1);
      color: $error;
    }

    &--default {
      background: $bg-subtle;
      color: $text-secondary;
    }
  }

  &__details {
    display: flex;
    gap: 40px;
    padding-left: 24px;
    border-left: 1px solid $border-color;

    @media (max-width: 768px) {
      padding-left: 0;
      border-left: none;
      border-top: 1px solid $border-color;
      padding-top: 16px;
    }

    @media (max-width: 480px) {
      flex-direction: column;
      gap: 16px;
    }
  }

  &__detail {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 120px;
  }

  &__label {
    font-size: 12px;
    color: $text-muted;
  }

  &__value {
    font-size: 14px;
    font-weight: 500;
    color: $text-primary;
  }

  &__link {
    font-size: 13px;
    font-weight: 500;
    color: $accent-primary;
    text-decoration: underline;
    margin-top: 12px;
    transition: color 0.2s ease;

    &:hover {
      color: darken($accent-primary, 10%);
    }
  }

  &__delete {
    font-size: 13px;
    font-weight: 500;
    color: $error;
    text-decoration: underline;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    margin-top: 12px;
    text-align: left;
    transition: color 0.2s ease;

    &:hover {
      color: darken($error, 10%);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
